import * as Location from 'expo-location';
import Analytics from '@/utils/analytics';
import ArrowLeft from '@assets/icons/arrow-left.svg';
import DeleteIcon from '@assets/icons/delete.svg';
import EllipsisIcon from '@assets/icons/ellipsis.svg';
import EmployeesIcon from '@assets/icons/employees.svg';
import i18n from '@/utils/i18n';
import NoteIcon from '@assets/icons/note.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import RestaurantMenuIcon from '@assets/icons/restaurant-menu.svg';
import TrashIcon from '@assets/icons/trash-small.svg';
import { ACCOUNT_FIELDS_FRAGMENT } from './edit-account-screen';
import { AccountDetailsMap } from '@/components/locations/account-details-map';
import { ActivityIndicator } from '@/components/activity-indicator';
import { ActivityList } from '@/components/locations/activity-list';
import { ActivityListItem, YelpMenuItem } from '@/components/locations/types';
import { AddressInputType } from '@domain/addresses/types/types';
import { AnalyticsPanel } from '@/components/locations/analytics-panel';
import {
  Animated,
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Avatar, createAvatarProps } from '@usepepper/ui/atoms/avatar';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  BusinessHours,
  RestaurantBusinessHours,
} from '@/components/restaurant-business-hours';
import { Button } from '@/components/button';
import { ChatStatus } from '@/enums';
import {
  clampScrollOffset,
  isApproachingEndOfContent,
} from '@/utils/scrolling';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, shadow, TYPOGRAPHY } from '@/styles';
import { formatAddressForInput } from '@/utils/address';
import {
  formatPhoneNumber,
  isNotMaliPhoneNumber,
  onCallMobilePhone,
} from '@/utils/phone-number';
import { getFormattedDistanceBetween } from '@/utils/distance';
import { gql } from '@apollo/client';
import { ICON_BUTTON_HIT_SLOP } from '@/components/navigation-header-content';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { KeyboardScope } from '@/platform/shortcuts/operator-shortcuts';
import { ListSection } from '@/components/recycler-list';
import { LOCATION_MANAGER_ROUTES } from '@/navigators/routes';
import { LocationBottomActions } from '@/components/locations/location-bottom-actions';
import { LocationManagerStackParams } from '@/navigators/types';
import {
  Locations_GetAssignedSalesRepInfoQuery,
  Locations_GetAssignedSalesRepInfoQueryVariables,
  Locations_GetBusinessDetailsQuery,
  Locations_GetBusinessDetailsQueryVariables,
  Locations_GetSupplierAndRestaurantFieldsQuery,
  Locations_GetSupplierAndRestaurantFieldsQueryVariables,
} from '@/types';
import { MenuOptionsActionSheet } from '@/components/locations/menu-options-action-sheet';
import { militaryTimeToHourAndMinutes } from '@/utils/date';
import { ModalContainer } from '@/components/modal-container';
import { NewNoteActionSheet } from '@/components/locations/new-note-action-sheet';
import { ObiItemsOfInterest } from '@/components/locations/obi-items-of-interest';
import { RemoveProspectConfirmationActionSheet } from '@/components/locations/remove-prospect-confirmation-action-sheet';
import { StackScreenProps } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallbackOnce } from '@/hooks/use-callback-once';
import { useComponentFocus } from '@/hooks/use-component-focus';
import {
  useGetDynamicConfigValueForChat,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useLayoutContext } from '@/contexts/layout-context';
import { usePaginatedCrmActivitiesQuery } from '@/hooks/use-paginated-crm-activities-query';
import { useSupplierRemoveRestaurant } from '@/hooks/use-supplier-remove-restaurant';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

const ACTION_BUTTON_HEIGHT = 54;
const ACTION_BUTTONS_PADDING_BOTTOM_MOBILE = 40;
const ACTION_BUTTONS_PADDING_BOTTOM_DESKTOP = 24;

export const GET_BUSINESS_DETAILS_QUERY = gql`
  query Locations_GetBusinessDetails(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getBusinessDetails(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      hours {
        open {
          day
          end
          is_overnight
          start
        }
      }
      menu_items {
        category
        ingredients
        item_name
      }
      phone_number
    }
  }
`;

export const GET_SUPPLIER_AND_RESTAURANT_FIELDS_QUERY = gql`
  query Locations_GetSupplierAndRestaurantFields(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $unplacedOrderStatuses: [String!]!
  ) {
    supplier: businesses_by_pk(uuid: $supplierUUID) {
      address {
        country_code
        uuid
      }
      uuid
    }

    restaurant: businesses_by_pk(uuid: $restaurantUUID) {
      ...Locations_AccountFields
    }
  }

  ${ACCOUNT_FIELDS_FRAGMENT}
`;

export const GET_SALES_REP_INFO_QUERY = gql`
  query Locations_GetAssignedSalesRepInfo($chatUUID: uuid!) {
    sales_rep_chat_relations(where: { chat_uuid: { _eq: $chatUUID } }) {
      employee {
        name
        uuid
      }
    }
  }
`;

type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.ViewAccount
>;

export const ViewAccountScreen: React.FC<Props> = ({ navigation, route }) => {
  const {
    onClose,
    onRemoveRestaurant,
    restaurantName,
    restaurantUUID,
    supplierUUID,
  } = route.params;
  const { showErrorToast } = useToasts();
  const { isSmallLayout } = useLayoutContext();
  const [styles, theme] = useStyles();

  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const { restaurantRelationships, user } = useAuthenticatedUserContext();
  const { disableScope, enableScope } = useKeyboardHotkeysContext();

  const relationship = useMemo(() => {
    return restaurantRelationships.find(
      (restaurant) => restaurant.uuid === restaurantUUID,
    );
  }, [restaurantRelationships, restaurantUUID]);

  const actionsPadding = isSmallLayout
    ? ACTION_BUTTONS_PADDING_BOTTOM_MOBILE
    : ACTION_BUTTONS_PADDING_BOTTOM_DESKTOP;

  const [showNoteActionSheet, setShowNoteActionSheet] = useState(false);
  const [showMenuOptionsActionSheet, setShowMenuOptionsActionSheet] =
    useState(false);
  const [showMoreActionSheet, setShowMoreActionSheet] = useState(false);
  const [showRemoveProspectModal, setShowRemoveProspectModal] = useState(false);
  const [coordinate, setCoordinate] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [maxScrollDistance, setMaxScrollDistance] = useState(0);

  const scrollY = useRef(new Animated.Value(0)).current;

  const {
    isRestaurantBeingRemoved,
    removeRestaurant,
    removeRestaurantError,
    removeRestaurantWithAlert,
  } = useSupplierRemoveRestaurant();

  useEffect(() => {
    if (removeRestaurantError) {
      showErrorToast();
    }
  }, [removeRestaurantError, showErrorToast]);

  const onRemoveProspect = () => {
    Analytics.track('view_account_screen.remove_prospect_button', {
      restaurantUUID,
      supplierUUID,
    });
    if (!supplierUUID) {
      return;
    }
    if (Platform.OS === 'web') {
      setShowRemoveProspectModal(true);
    } else {
      removeRestaurantWithAlert(
        restaurantUUID,
        restaurantName,
        supplierUUID,
        () => {
          onRemoveRestaurant?.(restaurantUUID);
          navigation.goBack();
        },
      );
    }
  };

  useEffect(() => {
    (async () => {
      let { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== Location.PermissionStatus.GRANTED) {
        return;
      }

      let location = await Location.getCurrentPositionAsync({});
      setCoordinate({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      });
    })();
  }, []);

  useComponentFocus({
    onBeforeRemove: useCallback(() => {
      onClose?.();
    }, [onClose]),
  });

  const onError = useCallbackOnce(() => {
    showErrorToast();
    navigation.goBack();
  });

  const { data: businessDetailsData } = useWrappedQuery<
    Locations_GetBusinessDetailsQuery,
    Locations_GetBusinessDetailsQueryVariables
  >(GET_BUSINESS_DETAILS_QUERY, {
    skip: !supplierUUID,
    variables: {
      restaurantUUID,
      supplierUUID: supplierUUID!,
    },
  });

  const {
    data: supplierAndRestaurantData,
    error,
    hasResponse,
  } = useWrappedQuery<
    Locations_GetSupplierAndRestaurantFieldsQuery,
    Locations_GetSupplierAndRestaurantFieldsQueryVariables
  >(GET_SUPPLIER_AND_RESTAURANT_FIELDS_QUERY, {
    skip: !supplierUUID,
    variables: {
      restaurantUUID,
      supplierUUID: supplierUUID!,
      unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
    },
  });

  const { data: salesRepInfoData } = useWrappedQuery<
    Locations_GetAssignedSalesRepInfoQuery,
    Locations_GetAssignedSalesRepInfoQueryVariables
  >(GET_SALES_REP_INFO_QUERY, {
    skip: !supplierAndRestaurantData?.restaurant?.restaurant_chats?.[0]?.uuid,
    variables: {
      chatUUID:
        supplierAndRestaurantData?.restaurant?.restaurant_chats?.[0]?.uuid!,
    },
  });

  useEffect(() => {
    if (error || !supplierUUID) {
      onError();
      return;
    }

    if (!supplierAndRestaurantData) {
      return;
    }

    if (
      !supplierAndRestaurantData.restaurant ||
      !supplierAndRestaurantData.restaurant.restaurant_chats?.[0] ||
      !supplierAndRestaurantData.restaurant.address
    ) {
      onError();
      return;
    }
  }, [supplierAndRestaurantData, error, onError, supplierUUID]);

  const businessHours: BusinessHours[] = useMemo(() => {
    const hours = businessDetailsData?.getBusinessDetails?.hours?.[0];
    if (!hours) {
      return [];
    }

    return hours.open.map((entry) => {
      const [startHour, startMinute] = militaryTimeToHourAndMinutes(
        entry.start,
      );
      const [endHour, endMinute] = militaryTimeToHourAndMinutes(entry.end);
      const entryDay = (entry.day + 1) % 7; // Yelp starts on Monday, we start on Sunday
      return {
        endDay: entry.is_overnight ? (entryDay + 1) % 7 : entryDay,
        endHour,
        endMinute,
        startDay: entryDay,
        startHour,
        startMinute,
      };
    });
  }, [businessDetailsData?.getBusinessDetails?.hours]);

  const restaurant = supplierAndRestaurantData?.restaurant;
  const chat = restaurant?.restaurant_chats?.[0];
  const address: AddressInputType | undefined = restaurant?.address
    ? formatAddressForInput(restaurant.address)
    : undefined;

  const countryCode =
    supplierAndRestaurantData?.supplier?.address?.country_code;
  const menuItems = (businessDetailsData?.getBusinessDetails?.menu_items ??
    []) as YelpMenuItem[];

  const {
    fetchingMore: fetchingMoreActivities,
    fetchMore: fetchMoreActivities,
    hasMore: hasMoreActivities,
    hasResponse: hasActivitiesResponse,
    loading: loadingActivities,
    data: activitiesData,
    refresh: refreshActivities,
    refreshing: refreshingActivities,
  } = usePaginatedCrmActivitiesQuery(chat?.uuid);

  const activities = useMemo<ListSection<ActivityListItem>[]>(() => {
    return [
      {
        data: activitiesData
          .filter(
            (activity) =>
              activity?.crm_activity_notes !== undefined &&
              activity?.crm_activity_notes !== null,
          )
          .map<ActivityListItem>((activity) => ({
            metadata: {
              createdAt: activity.created_at,
              employeeName: activity.employee ? activity.employee.name : null,
              reminderAt: activity.crm_activity_reminders?.[0]
                ? activity.crm_activity_reminders?.[0].remind_at
                : null,
            },
            title: activity.crm_activity_notes!.content,
            value: activity.uuid,
          })),
        key: null,
      },
    ];
  }, [activitiesData]);

  useEffect(() => {
    enableScope(KeyboardScope.ACTION_SHEET);
    return () => {
      disableScope(KeyboardScope.ACTION_SHEET);
    };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useKeyboardShortcuts(
    // so this isn't necessarily a KeyboardShortcut, but it is an action that we want to handle
    // in the event that a user wants to close the 'modal' with the escape key
    [Key.Escape],
    () => navigation.goBack(),
    [],
    {},
    true,
    KeyboardScope.ACTION_SHEET,
  );

  // check if any chat has menu picture to OG enabled
  const anyChatHasMenuImageOGEnabled = useMemo(() => {
    return restaurantRelationships.some((relationship) => {
      return getDynamicConfigValueForChat(
        relationship.chatUUIDs[0],
        'menu_image_og_generation_enabled',
        false,
      );
    });
  }, [restaurantRelationships, getDynamicConfigValueForChat]);

  // check if the _current_ chat has menu picture to OG enabled
  const menuImageOGGenerationEnabled = chat
    ? (getDynamicConfigValueForChat(
        chat.uuid,
        'menu_image_og_generation_enabled',
        false,
      ) ?? false) // ensure it defaults to false if undefined
    : false;

  // check if the OBI items of interested are enabled
  const salesRepConfig = chat
    ? getDynamicConfigValueForChat(chat.uuid, 'sales_rep_config', undefined)
    : undefined;
  const isOBIItemsOfInterestEnabled = salesRepConfig
    ? salesRepConfig.obi_settings?.items_of_interest_enabled
    : false;

  const isProspect = relationship?.chatStatus === ChatStatus.PROSPECT;

  const shouldShowOnlineMenuOption = menuItems.length > 0;
  const shouldShowTakePictureOption = menuImageOGGenerationEnabled;

  const shouldShowMenuButton =
    shouldShowOnlineMenuOption || shouldShowTakePictureOption;

  const viewAccountMenu = () => {
    navigation.navigate(LOCATION_MANAGER_ROUTES.ViewAccountMenu, {
      chatUUID: chat?.uuid,
      menu: menuItems,
      restaurantName,
      restaurantUUID,
      supplierUUID: supplierUUID!,
    });
  };

  const phoneNumber = businessDetailsData?.getBusinessDetails?.phone_number;

  const salesRepEmployee =
    salesRepInfoData?.sales_rep_chat_relations?.[0]?.employee;

  const showAssignedSalesRep =
    useSupplierDynamicConfigValue('assigned_sales_rep_visible') &&
    salesRepEmployee &&
    user.uuid !== salesRepEmployee.uuid;

  const bottomActions = [
    ...(shouldShowMenuButton
      ? [
          {
            accessibilityLabel: i18n.t('view_menu'),
            Icon: RestaurantMenuIcon,
            iconProps: {
              fill: theme.sk.color.icon.primary,
            },
            onPress: () => {
              if (anyChatHasMenuImageOGEnabled) {
                // show the action sheet for consistency across chats,
                // even if the current chat does not have menu picture to OG enabled
                setShowMenuOptionsActionSheet(true);
              } else if (menuItems.length > 0) {
                // navigate directly to the online menu
                viewAccountMenu();
              }
            },
          },
        ]
      : []),
    {
      accessibilityLabel: i18n.t('view_employees'),
      Icon: EmployeesIcon,
      iconProps: {
        fill: theme.sk.color.icon.primary,
      },
      onPress: () => {
        navigation.navigate(LOCATION_MANAGER_ROUTES.ViewAccountEmployees, {
          restaurantName,
          restaurantUUID,
          supplierUUID: supplierUUID!,
        });
      },
    },
    ...(relationship?.showCrmActivities
      ? [
          {
            accessibilityLabel: i18n.t('add_note'),

            Icon: NoteIcon,
            iconProps: {
              fill: theme.sk.color.icon.primary,
              height: 24,
              width: 24,
            },
            onPress: () => setShowNoteActionSheet(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <ModalContainer
        navigationHeaderProps={{
          actions: isProspect
            ? [
                {
                  component: (
                    <TouchableOpacity
                      onPress={() => {
                        setShowMoreActionSheet(true);
                      }}
                      style={styles.actionButton}
                      testID="more-actions-button"
                    >
                      <EllipsisIcon height={24} width={24} />
                    </TouchableOpacity>
                  ),
                },
              ]
            : [],
          BackButtonIcon: isSmallLayout ? ArrowLeft : DeleteIcon,
          showBackButton: true,
          showShadow: false,
        }}
        showLargeLayoutCloseButton={false}
        transparentNavigationHeader
      >
        {hasResponse && !isRestaurantBeingRemoved && relationship ? (
          <>
            <ScrollView
              alwaysBounceVertical={false}
              onScroll={({ nativeEvent }) => {
                const offsetY = nativeEvent.contentOffset.y;
                const nextMaxScrollDistance =
                  nativeEvent.contentSize.height -
                  nativeEvent.layoutMeasurement.height;

                if (!maxScrollDistance) {
                  setMaxScrollDistance(nextMaxScrollDistance);
                }

                if (isApproachingEndOfContent(nativeEvent, 100)) {
                  if (hasMoreActivities && !fetchingMoreActivities) {
                    fetchMoreActivities();
                  }
                }

                scrollY.setValue(clampScrollOffset(offsetY, maxScrollDistance));
              }}
              scrollEventThrottle={16}
              testID="view-account-screen-scroll-view"
            >
              {address ? <AccountDetailsMap address={address} /> : null}
              <View style={styles.base}>
                <View style={styles.restaurantNameRow}>
                  <Text style={[TYPOGRAPHY.h2, styles.restaurantName]}>
                    {restaurant?.name || ''}
                  </Text>
                  <Button
                    hitSlop={ICON_BUTTON_HIT_SLOP}
                    onPress={() => {
                      navigation.navigate(LOCATION_MANAGER_ROUTES.EditAccount, {
                        accountID: chat?.restaurant_account_id || '',
                        restaurantAddress: address || null,
                        restaurantName,
                        restaurantNotes: chat?.restaurant_notes || '',
                        restaurantRouteStop:
                          chat?.chat_route_metadata?.name || '',
                        restaurantUUID,
                        supplierUUID: supplierUUID!,
                      });
                    }}
                    size="small"
                    trackingData={{
                      name: 'view_account_screen.edit_button',
                      properties: {
                        restaurantUUID,
                      },
                    }}
                    type="tertiary"
                  >
                    {i18n.t('edit')}
                  </Button>
                </View>
                {chat?.restaurant_account_id ? (
                  <Text style={[styles.accountInfoRow, styles.restaurantID]}>
                    {chat.restaurant_account_id}
                  </Text>
                ) : null}
                {chat?.chat_route_metadata?.name ? (
                  <Text style={[styles.accountInfoRow, styles.routeStop]}>
                    {i18n.t('route_stop_display', {
                      name: chat.chat_route_metadata.name,
                    })}
                  </Text>
                ) : null}
                {chat?.restaurant_notes ? (
                  <Text style={[styles.accountInfoRow, styles.restaurantNote]}>
                    {chat.restaurant_notes}
                  </Text>
                ) : null}
                {phoneNumber && isNotMaliPhoneNumber(phoneNumber) ? (
                  <TouchableOpacity
                    disabled={Platform.OS === 'web'}
                    onPress={async () => {
                      await onCallMobilePhone(phoneNumber);
                    }}
                  >
                    <View style={styles.rowGap} />
                    <Text style={styles.accountInfoRow}>
                      {formatPhoneNumber(phoneNumber)}
                    </Text>
                  </TouchableOpacity>
                ) : null}
                {address?.address ? (
                  <Text style={styles.accountInfoRow}>{address.address}</Text>
                ) : null}
                {address && coordinate && countryCode ? (
                  <Text style={styles.distance}>
                    {getFormattedDistanceBetween(
                      coordinate.latitude,
                      coordinate.longitude,
                      address.latitude,
                      address.longitude,
                      countryCode,
                    )}
                  </Text>
                ) : null}
                {businessHours.length > 0 ? (
                  <View style={styles.businessHours}>
                    <RestaurantBusinessHours businessHours={businessHours} />
                  </View>
                ) : null}
                {showAssignedSalesRep ? (
                  <View style={styles.salesRepInfoContainer}>
                    <Avatar
                      {...createAvatarProps(
                        salesRepEmployee.name,
                        salesRepEmployee.uuid,
                        2,
                      )}
                      size="small"
                    />
                    <Typography variant="paragraph-md-600">
                      {salesRepEmployee?.name}
                    </Typography>
                  </View>
                ) : null}
              </View>
              {chat ? (
                <View style={styles.analyticsPanel}>
                  <AnalyticsPanel chat={chat} supplierUUID={supplierUUID!} />
                </View>
              ) : null}

              {isOBIItemsOfInterestEnabled && supplierUUID ? (
                <ObiItemsOfInterest
                  restaurantUUID={restaurantUUID}
                  supplierUUID={supplierUUID}
                />
              ) : null}

              {relationship?.showCrmActivities && chat?.uuid ? (
                <ActivityList
                  activities={activities}
                  fetchingMore={fetchingMoreActivities}
                  hasResponse={hasActivitiesResponse}
                  loading={loadingActivities}
                  refresh={refreshActivities}
                  refreshing={refreshingActivities}
                  style={styles.activityList}
                />
              ) : null}
              <View
                // Add a spacer to the bottom of the ScrollView so that the floating
                // icons don't overlap with the text when the view is scrolled down.
                style={{
                  height: ACTION_BUTTON_HEIGHT + actionsPadding + 16,
                }}
              />
            </ScrollView>
            <LocationBottomActions
              bottomActions={bottomActions}
              maxScrollDistance={maxScrollDistance}
              scrollY={scrollY}
            />
          </>
        ) : (
          <View style={styles.loadingIndicator}>
            <ActivityIndicator />
          </View>
        )}
      </ModalContainer>
      {showMenuOptionsActionSheet ? (
        <MenuOptionsActionSheet
          onClose={() => setShowMenuOptionsActionSheet(false)}
          onNavigateToMenuCameraScreen={() => {
            navigation.navigate(LOCATION_MANAGER_ROUTES.MenuCamera, {
              restaurantName,
              restaurantUUID,
              supplierUUID: supplierUUID!,
            });
          }}
          onNavigateToViewMenu={viewAccountMenu}
          showOnlineMenuOption={shouldShowOnlineMenuOption}
          showTakePictureOption={shouldShowTakePictureOption}
        />
      ) : null}
      {showNoteActionSheet ? (
        <NewNoteActionSheet
          chatUUID={chat!.uuid}
          onClose={() => {
            setShowNoteActionSheet(false);
          }}
        />
      ) : null}
      {showMoreActionSheet && relationship && supplierUUID ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowMoreActionSheet(false)}
        >
          {isProspect ? (
            <TouchableOpacity
              onPress={() => {
                setShowMoreActionSheet(false);
                onRemoveProspect();
              }}
              style={styles.row}
            >
              <TrashIcon
                height={24}
                stroke={theme.colors.negative}
                width={24}
              />
              <Text style={styles.remove}>
                {i18n.t('remove_prospect.button')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </BaseActionSheet>
      ) : null}
      {showRemoveProspectModal ? (
        <RemoveProspectConfirmationActionSheet
          onCancel={() => setShowRemoveProspectModal(false)}
          onConfirm={async () => {
            setShowRemoveProspectModal(false);
            navigation.goBack();
            onRemoveRestaurant?.(restaurantUUID);
            await removeRestaurant(restaurantUUID, supplierUUID!);
          }}
          restaurantName={restaurantName}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  accountInfoRow: {
    ...TYPOGRAPHY.h4,
    marginTop: 4,
  },
  actionButton: {
    alignItems: 'center',
    ...shadow({
      elevation: 5,
      opacity: 0.1,
      radius: 24,
      x: 0,
      y: 12,
    }),
    backgroundColor: theme.colors.white,
    borderRadius: 32 / 2,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  activityList: {
    marginHorizontal: 24,
  },
  analyticsPanel: {
    marginTop: 16,
  },
  base: {
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: 16,
  },
  businessHours: {
    paddingVertical: 4,
  },
  distance: {
    ...FONTS.primarySemiBold,
    color: theme.colors.darkGreen,
    fontSize: 12,
    paddingVertical: 4,
  },
  loadingIndicator: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  remove: {
    ...TYPOGRAPHY.pSemiBold,
    color: theme.colors.negative,
  },
  restaurantID: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
  },
  restaurantName: {
    alignSelf: 'flex-end',
    flex: 1,
    lineHeight: 24,
    marginTop: 8,
  },
  restaurantNameRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  restaurantNote: {
    color: theme.sk.color.text.tertiary,
  },
  routeStop: {
    color: theme.sk.color.text.tertiary,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    paddingVertical: 16,
  },
  rowGap: {
    height: 16,
  },
  salesRepInfoContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    marginTop: 4,
  },
}));
